import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./css/RicercaServizi.css";
import SupplierImage from "../components/SupplierImage";
import {track} from '../analytics/service';
import {
  getUserInfo,
  listAddressOptions,
  resolveOrganizationContext,
  getFornitoreCompleto,
} from "../Admin/adminApi";
import StarRating from "../components/StarRating";
import ReviewsDrawer from "../components/ReviewsDrawer";
import SupplierHoursButton from "../components/SupplierHoursButton";
import toast from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import ValidatedCityInput from "../components/ValidateCityInput";
import {
  SERVICE_CATEGORIES,
  DEFAULT_SERVICE_KEY,
  getServiceFlags,
} from "../config/serviceCategories";
import { hapticLight, hapticMedium } from "../utils/haptics";

function normalizeToIsoDateOnly(value) {
  if (!value) return "";

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateIt(value) {
  const iso = normalizeToIsoDateOnly(value);
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}




function buildClosureBadges(closureState) {
  if (!closureState) return [];

  const pickup = closureState.pickup_closed
    ? closureState.pickup_closed_until
    : null;
  const delivery = closureState.delivery_closed
    ? closureState.delivery_closed_until
    : null;

  if (!pickup && !delivery) return [];

  if (pickup && delivery && pickup === delivery) {
    return [`Temporaneamente chiuso fino al ${formatDateIt(pickup)}`];
  }

  const out = [];
  if (pickup) out.push(`Ritiro sospeso fino al ${formatDateIt(pickup)}`);
  if (delivery) out.push(`Consegna sospesa fino al ${formatDateIt(delivery)}`);
  return out;
}

function addressOptionValue(a) {
  return `${a.source_type}:${a.source_id}`;
}

export default function RicercaServizi() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = React.useMemo(() => new URLSearchParams(search), [search]);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const qsCity = params.get("citta") || "";
  const qsServizio = (
    params.get("servizio") ||
    params.get("tipo") ||
    ""
  ).toLowerCase();
  const qsAuto = params.get("auto") === "1";

  const [lavaggio, setLavaggio] = useState(false);
  const [stiratura, setStiratura] = useState(true);

  const [addresses, setAddresses] = useState([]);
  const [selectedId, setSelectedId] = useState(null); // string "user:12" | "organization:7" | null
  const [addrSelect, setAddrSelect] = useState("");
  const [showSearchedCityOption, setShowSearchedCityOption] = useState(false);

  const [organizationContext, setOrganizationContext] = useState(null);
  const [organizationSupplierOnly, setOrganizationSupplierOnly] =
    useState(null);

  const [cityInput, setCityInput] = useState("");
  const [cityValid, setCityValid] = useState(null);
  const searchedCityLabel = useMemo(() => {
    const city = (qsCity || "").trim();
    if (!city) return "";
    return `🔎 ${city}`;
  }, [qsCity]);

  const hasSearchedCity = useMemo(() => {
    return !!(qsCity && qsCity.trim());
  }, [qsCity]);

  const selectValue = useMemo(() => {
    if (showSearchedCityOption && hasSearchedCity) return "__SEARCH_CITY__";
    return selectedId || "";
  }, [showSearchedCityOption, hasSearchedCity, selectedId]);

  const [citta, setCitta] = useState(qsCity);
  const [fornitori, setFornitori] = useState([]);
  const [ricercaAvviata, setRicercaAvviata] = useState(false);
  const [openHoursFor, setOpenHoursFor] = useState(null);
  const [ratings, setRatings] = useState({});
  const [openReviews, setOpenReviews] = useState(false);
  const [reviewsSupplier, setReviewsSupplier] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [myReviews, setMyReviews] = useState({});

  const [serviceAvailability, setServiceAvailability] = useState({
    lavaggio: null,
    stiratura: null,
    entrambi: null,
  });

  const handleVaiAbbonamenti = useCallback(
    (fornitore) => {
      const fornitoreId =
        fornitore?.fornitore_id ||
        fornitore?.id_fornitore ||
        fornitore?.supplier_id ||
        fornitore?.id;

      if (!fornitoreId) {
        toast.error("Fornitore non valido per gli abbonamenti");
        return;
      }

      navigate(`/abbonamenti?fornitore_id=${fornitoreId}`);
    },
    [navigate]
  );

  useEffect(() => {
    (async () => {
      try {
        const list = await listAddressOptions(token);
        const safeList = Array.isArray(list) ? list : [];
        setAddresses(safeList);

        const cityFromQs = (qsCity || "").trim();
        const normQsCity = cityFromQs.toLowerCase();

        // CASO: città arrivata in querystring
        if (cityFromQs) {
          const sameCity = safeList.filter(
            (a) =>
              String(a.citta || "")
                .trim()
                .toLowerCase() === normQsCity,
          );

          if (sameCity.length > 0) {
            const preferred =
              sameCity.find((a) => a.source_type === "user" && a.active) ||
              sameCity.find((a) => a.active) ||
              sameCity[0];

            const nextValue = addressOptionValue(preferred);

            setSelectedId(nextValue);
            setAddrSelect(nextValue);
            setShowSearchedCityOption(false);
            setCitta(cityFromQs);
            return;
          }

          // Nessun indirizzo in quella città:
          // mostro la città cercata ma senza indirizzo selezionato
          setSelectedId(null);
          setAddrSelect("__SEARCH_CITY__");
          setShowSearchedCityOption(true);
          setCitta(cityFromQs);
          return;
        }

        // CASO: nessuna città in QS -> default indirizzo attivo
        const active =
          safeList.find((a) => a.source_type === "user" && a.active) ||
          safeList.find((a) => a.active) ||
          safeList[0] ||
          null;

        if (!active) {
          setSelectedId(null);
          setAddrSelect("");
          setShowSearchedCityOption(false);
          setCitta("");
          return;
        }

        const nextValue = addressOptionValue(active);

        setSelectedId(nextValue);
        setAddrSelect(nextValue);
        setShowSearchedCityOption(false);
        setCitta(active.citta || "");
      } catch (e) {
        console.error("Caricamento address options fallito:", e);
        setAddresses([]);

        const cityFromQs = (qsCity || "").trim();
        if (cityFromQs) {
          setSelectedId(null);
          setAddrSelect("__SEARCH_CITY__");
          setShowSearchedCityOption(true);
          setCitta(cityFromQs);
        } else {
          setSelectedId(null);
          setAddrSelect("");
          setShowSearchedCityOption(false);
          setCitta("");
        }
      }
    })();
  }, [token, qsCity]);

  const activeAddress = useMemo(() => {
    if (!addrSelect || addrSelect === "__SEARCH_CITY__") return null;
    return addresses.find((a) => addressOptionValue(a) === addrSelect) || null;
  }, [addresses, addrSelect]);

  const isOrganizationMode = activeAddress?.source_type === "organization";

  const runSearch = async (opts = {}) => {
    const currentAddress = Object.prototype.hasOwnProperty.call(opts, "address")
      ? opts.address
      : activeAddress;

    const isOrgMode = currentAddress?.source_type === "organization";

    const typedCity = (cityInput && cityInput.trim()) || "";
    const cityFromAddr = currentAddress?.citta || "";
    const baseCity = opts.citta ?? (typedCity || citta || cityFromAddr);

    const lav = Object.prototype.hasOwnProperty.call(opts, "lavaggio")
      ? opts.lavaggio
      : lavaggio;
    const sti = Object.prototype.hasOwnProperty.call(opts, "stiratura")
      ? opts.stiratura
      : stiratura;

    if (!baseCity && !isOrgMode) {
      toast.error("Città non specificata");
      return;
    }

    if (typedCity && cityValid === false) {
      toast.error("Controlla l'ortografia della città");
      return;
    }

    try {
      setIsLoading(true);

      if (isOrgMode && currentAddress) {
        const ctx = await resolveOrganizationContext(
          {
            source_type: currentAddress.source_type,
            source_id: Number(currentAddress.source_id),
          },
          token,
        );

        setOrganizationContext(ctx || null);

        const supplierId = ctx?.supplier?.id;
        if (!supplierId) {
          setOrganizationSupplierOnly(null);
          setFornitori([]);
          track('service_search_completed',{result_count:0,outcome:'success'},opts.analyticsKey);
          setRicercaAvviata(true);
          toast.error("Nessun fornitore associato all'organizzazione");
          return;
        }

        const supplier = await getFornitoreCompleto(supplierId, token);
        setOrganizationSupplierOnly(supplier || null);

        const normalizedSupplier = supplier
          ? [
            {
              id_fornitore:
                supplier.id ?? supplier.id_fornitore ?? supplier.fornitore_id,
              nome:
                supplier.nome_fornitore ??
                supplier.nome ??
                supplier.name ??
                "Fornitore",
              cover_url:
                supplier.cover_url ??
                supplier.logo_url ??
                "/images/ciao_stiro.png",
              has_subscription_plans: Array.isArray(supplier.servizi)
                ? supplier.servizi.some((s) => !!s.has_subscription_plans)
                : !!supplier.has_subscription_plans,
              items: Array.isArray(supplier.servizi)
                ? supplier.servizi.map((s) => ({
                  ...s,
                  fasce_orarie: Array.isArray(supplier.fasce_orarie)
                    ? supplier.fasce_orarie
                    : [],
                  closure_state: supplier.closure_state || null,
                }))
                : [],
            },
          ]
          : [];

        setFornitori(normalizedSupplier);
        track('service_search_completed',{result_count:normalizedSupplier.length,outcome:'success'},opts.analyticsKey);
        setRicercaAvviata(true);
        setOpenHoursFor(null);

        const g = normalizedSupplier[0];
        if (g?.id_fornitore) {
          try {
            const r = await fetch(
              `${API_BASE}/api/fornitori/${g.id_fornitore}/reviews/summary`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            const js = r.ok ? await r.json() : {};
            setRatings((prev) => ({
              ...prev,
              [g.id_fornitore]: { avg: js.avg || 0, count: js.count || 0 },
            }));
          } catch {
            setRatings((prev) => ({
              ...prev,
              [g.id_fornitore]: { avg: 0, count: 0 },
            }));
          }

          if (token) {
            try {
              const me = await fetch(
                `${API_BASE}/api/fornitori/${g.id_fornitore}/reviews/me`,
                { headers: { Authorization: `Bearer ${token}` } },
              );
              const meJs = me.ok ? await me.json() : null;
              setMyReviews((prev) => ({
                ...prev,
                [g.id_fornitore]: meJs,
              }));
            } catch {
              setMyReviews((prev) => ({
                ...prev,
                [g.id_fornitore]: null,
              }));
            }
          }
        }

        return;
      }

      setOrganizationContext(null);
      setOrganizationSupplierOnly(null);

      const query = new URLSearchParams({
        citta: baseCity,
        lavaggio: String(lav),
        stiratura: String(sti),
      }).toString();

      const res = await fetch(
        `${API_BASE}/api/servizi/ricerca?${query}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error("Errore:", err.error || "Errore sconosciuto");
        toast.error("Errore nella ricerca dei servizi. Riprova.");
        return;
      }

      const raw = await res.json();
      const rows = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.results)
          ? raw.results
          : [];

      const groupedObj = rows.reduce((acc, r) => {
        const id = r.id_fornitore ?? r.fornitore_id ?? r.id;
        const name = r.nome_fornitore ?? r.nome ?? "Fornitore";
        const cover_url = r.cover_url ?? r.logo_url ?? "/images/ciao_stiro.png";
        const key = `${id}|${name}`;

        if (!acc[key]) {
          acc[key] = {
            id_fornitore: id,
            nome: name,
            cover_url,
            has_subscription_plans: !!r.has_subscription_plans,
            items: [],
          };
        } else if (r.has_subscription_plans) {
          acc[key].has_subscription_plans = true;
        }

        acc[key].items.push(r);

        acc[key].items.push(r);
        return acc;
      }, {});

      const groupedArr = Object.values(groupedObj);

      setFornitori(groupedArr);
      track('service_search_completed',{result_count:groupedArr.length,outcome:'success',category:lav&&sti?'entrambi':lav?'lavaggio':'stiratura'},opts.analyticsKey);
      setRicercaAvviata(true);
      setOpenHoursFor(null);

      groupedArr.forEach(async (g) => {
        try {
          const r = await fetch(
            `${API_BASE}/api/fornitori/${g.id_fornitore}/reviews/summary`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          const js = r.ok ? await r.json() : {};
          setRatings((prev) => ({
            ...prev,
            [g.id_fornitore]: { avg: js.avg || 0, count: js.count || 0 },
          }));
        } catch {
          setRatings((prev) => ({
            ...prev,
            [g.id_fornitore]: { avg: 0, count: 0 },
          }));
        }
      });

      if (token) {
        groupedArr.forEach(async (g) => {
          try {
            const me = await fetch(
              `${API_BASE}/api/fornitori/${g.id_fornitore}/reviews/me`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            const meJs = me.ok ? await me.json() : null;
            setMyReviews((prev) => ({ ...prev, [g.id_fornitore]: meJs }));
          } catch {
            setMyReviews((prev) => ({ ...prev, [g.id_fornitore]: null }));
          }
        });
      }
    } catch (err) {
      console.error("Errore fetch:", err);
      toast.error(err?.message || "Errore di rete o del server.");
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeSelected = async (e) => {
    const v = e.target.value;
    setAddrSelect(v);
    await hapticLight();

    if (v === "__SEARCH_CITY__") {
      const cityFromQs = (qsCity || "").trim();
      if (cityFromQs) {
        setCitta(cityFromQs);
        setOrganizationContext(null);
        setOrganizationSupplierOnly(null);
        runSearch({ citta: cityFromQs, address: null });
      }
      return;
    }

    setShowSearchedCityOption(false);
    setSelectedId(v);

    const addr = addresses.find((a) => addressOptionValue(a) === v);

    if (addr?.citta) {
      setCitta(addr.citta);
    }

    if (addr?.source_type === "organization") {
      setCityInput("");
      setCityValid(true);
      await runSearch({
        citta: addr?.citta || "",
        address: addr,
      });
      return;
    }

    setOrganizationContext(null);
    setOrganizationSupplierOnly(null);

    if (addr?.citta) {
      runSearch({
        citta: addr.citta,
        address: addr,
      });
    }
  };

  const effectiveCity = useMemo(() => {
    const typed = (cityInput && cityInput.trim()) || "";
    const fromAddr = activeAddress?.citta || "";
    return (typed || citta || fromAddr || "").trim();
  }, [cityInput, citta, activeAddress]);

  useEffect(() => {
    if (isOrganizationMode) {
      setServiceAvailability({
        lavaggio: true,
        stiratura: true,
        entrambi: true,
      });
      return;
    }

    if (!ricercaAvviata || !effectiveCity) return;

    let cancelled = false;

    (async () => {
      try {
        const combos = [
          { key: "lavaggio", lavaggio: true, stiratura: false },
          { key: "stiratura", lavaggio: false, stiratura: true },
          { key: "entrambi", lavaggio: true, stiratura: true },
        ];

        const next = {
          lavaggio: null,
          stiratura: null,
          entrambi: null,
        };

        for (const combo of combos) {
          const query = new URLSearchParams({
            citta: effectiveCity,
            lavaggio: String(combo.lavaggio),
            stiratura: String(combo.stiratura),
          }).toString();

          const res = await fetch(`${API_BASE}/api/servizi/ricerca?${query}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (!res.ok) continue;

          const raw = await res.json();
          const rows = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.results)
              ? raw.results
              : [];

          next[combo.key] = rows.length > 0;
        }

        if (!cancelled) {
          setServiceAvailability(next);
        }
      } catch (err) {
        console.error("Errore availability servizi:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [API_BASE, token, effectiveCity, ricercaAvviata]);

  useEffect(() => {
    const key = qsServizio || DEFAULT_SERVICE_KEY;
    const { lavaggio: lavFlag, stiratura: stiFlag } = getServiceFlags(key);

    setLavaggio(lavFlag);
    setStiratura(stiFlag);

    const cityFromQs = (qsCity || "").trim();
    if (cityFromQs) {
      setCitta(cityFromQs);
      setCityInput("");
      setShowSearchedCityOption(true);
    } else {
      setCitta(qsCity);
    }

    if (qsAuto && (cityFromQs || qsServizio)) {
      runSearch({
        analyticsKey:`auto:${search}`,
        citta: cityFromQs || undefined,
        lavaggio: lavFlag,
        stiratura: stiFlag,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, token]);

  useEffect(() => {
    if (citta) return;
    (async () => {
      try {
        const u = await getUserInfo(token);
        if (u?.citta) {
          setCitta(u.citta);
          if (qsAuto) {
            const key = qsServizio || DEFAULT_SERVICE_KEY;
            const { lavaggio: lavFlag, stiratura: stiFlag } =
              getServiceFlags(key);
            runSearch({
              analyticsKey:`auto:${search}`,
              citta: u.citta,
              lavaggio: lavFlag,
              stiratura: stiFlag,
            });
          }
        }
      } catch {
        // niente
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qsAuto, qsServizio, token, citta]);


  const hasMatchingAddressForQsCity = useMemo(() => {
    const cityFromQs = (qsCity || "").trim().toLowerCase();
    if (!cityFromQs) return false;

    return addresses.some(
      (a) =>
        String(a.citta || "")
          .trim()
          .toLowerCase() === cityFromQs,
    );
  }, [addresses, qsCity]);

  const addressLabel = useMemo(() => {
    if (activeAddress) {
      return activeAddress.display_name || activeAddress.label || "Indirizzo";
    }

    if ((qsCity || "").trim() && !hasMatchingAddressForQsCity) {
      return `Nessun indirizzo salvato a ${(qsCity || "").trim()}`;
    }

    return citta || "Scegli indirizzo";
  }, [activeAddress, qsCity, hasMatchingAddressForQsCity, citta]);

  const categoria = lavaggio && stiratura ? 3 : lavaggio ? 2 : 1;

  const goToScheda = useCallback(
    async (id, nome, cover_url) => {
      const selectedAddress = activeAddress ? {
        source_type: activeAddress.source_type,
        source_id: Number(activeAddress.source_id),
        label: activeAddress.display_name || activeAddress.label || "Indirizzo",
        indirizzo:
          activeAddress.display_name || activeAddress.label || "Indirizzo",
        street: activeAddress.street || "",
        cap: activeAddress.cap || "",
        citta: activeAddress.citta || "",
        provincia: activeAddress.provincia || "",
        active: !!activeAddress.active,
        organization_name: activeAddress.organization_name || "",
      } : null;

      await hapticMedium();
      navigate(`/servizi/${id}`, {
        state: {
          nome,
          categoria,
          cover_url,
          selectedAddress,
          organizationContext: organizationContext || null,
        },
      });
    },
    [navigate, categoria, activeAddress, organizationContext],
  );

  const gridClass = useMemo(() => {
    let cls = "risultati-grid";
    if (ricercaAvviata && !isLoading) {
      if (fornitori.length === 1) cls += " risultati-grid--n1";
      else if (fornitori.length === 2) cls += " risultati-grid--n2";
      else if (fornitori.length >= 3) cls += " risultati-grid--n3";
    }
    return cls;
  }, [ricercaAvviata, isLoading, fornitori.length]);

  const activeCatKey = useMemo(() => {
    const match = SERVICE_CATEGORIES.find(
      (c) => c.lavaggio === lavaggio && c.stiratura === stiratura,
    );
    return match ? match.key : DEFAULT_SERVICE_KEY;
  }, [lavaggio, stiratura]);

  const selectCategory = useCallback(
    async (cat) => {
      setLavaggio(cat.lavaggio);
      setStiratura(cat.stiratura);

      const sp = new URLSearchParams(search);
      sp.set("servizio", cat.key);
      navigate({ search: `?${sp.toString()}` }, { replace: true });

      await hapticLight();
      runSearch({
        analyticsKey:qsAuto?`auto:?${sp.toString()}`:undefined,
        lavaggio: cat.lavaggio,
        stiratura: cat.stiratura,
        address: activeAddress,
      });
    },
    [search, navigate, activeAddress, runSearch, qsAuto]
  );

  return (
    <div className="app">
      <header className="header safe">
        <div className="row">
          <div className="addrWrap">
            <div className="addr pressable" title="Cambia indirizzo">
              {addressLabel} ▾
            </div>

            <select
              className="addrSelect"
              aria-label="Seleziona indirizzo"
              value={addrSelect}
              onChange={onChangeSelected}
            >
              {showSearchedCityOption && (qsCity || "").trim() && (
                <option value="__SEARCH_CITY__">
                  🔍 {(qsCity || "").trim()}
                </option>
              )}

              {addresses.length === 0 && (
                <option value="">— Nessun indirizzo disponibile —</option>
              )}

              {addresses.map((a) => {
                const cityProv = [a.citta, a.provincia && `(${a.provincia})`]
                  .filter(Boolean)
                  .join(" ");

                const prefix = a.source_type === "organization" ? "🏢" : "🏠";
                const text =
                  `${prefix} ${a.display_name || a.label || "Indirizzo"}` +
                  (cityProv ? ` - ${cityProv}` : "");

                return (
                  <option
                    key={addressOptionValue(a)}
                    value={addressOptionValue(a)}
                  >
                    {text}
                  </option>
                );
              })}
            </select>
          </div>

        </div>
      </header>

      <section aria-label="Categorie">

        <div className="categories">
          {SERVICE_CATEGORIES.filter(
            (c) => serviceAvailability[c.key] !== false,
          ).map((c) => {
            const isActive = activeCatKey === c.key;
            return (
              <div
                className={`cat ${isActive ? "cat--active" : ""}`}
                key={c.key}
              >
                <button
                  className={`catCard pressable ${isActive ? "catCard--active" : ""
                    }`}
                  onClick={() => selectCategory(c)}
                  title={c.label}
                  aria-pressed={isActive}
                >
                  <div className="catImgWrap">
                    <img className="catImg" src={c.img} alt={c.label} />
                  </div>
                </button>
                <div
                  className={`catLabel ${isActive ? "catLabel--active" : ""}`}
                >
                  {c.label}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Chip filtri rapidi: nascosti finché non applicano un filtro reale
          (oggi chiamano solo runSearch() -> no-op). Riattivare quando i
          filtri Offerte / Consegna gratuita / Carta Fedeltà sono funzionanti. */}
      {false && (
        <div className="chips">
          <button className="chip pressable" onClick={() => runSearch()}>
            🏷️ Offerte
          </button>
          <button className="chip pressable" onClick={() => runSearch()}>
            🚚 Consegna gratuita
          </button>
          <button className="chip pressable" onClick={() => runSearch()}>
            🎁 Carta Fedeltà
          </button>
        </div>
      )}

      {isLoading && (
        <div className="risultati-grid mt-10" aria-live="polite">
          {Array.from({ length: 6 }).map((_, i) => (
            <div className="vendor skeleton-card" key={i}>
              <div className="skeleton-cover" />
              <div className="skeleton-body">
                <div className="skeleton-line lg" />
                <div className="skeleton-line" />
                <div className="skeleton-line sm" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && ricercaAvviata && (
        <div className={gridClass}>
          {Array.isArray(fornitori) && fornitori.length > 0 ? (
            fornitori.map((g) => {
              const r = ratings[g.id_fornitore] || { avg: 0, count: 0 };
              const isOpenHours = openHoursFor === g.id_fornitore;
              const closureState =
                g.items.find((it) => it.closure_state)?.closure_state || null;

              const closureBadges = buildClosureBadges(closureState);

              const map = new Map();
              g.items.forEach((s) => {
                (s.fasce_orarie || []).forEach((f) => {
                  const key = `${f.giorno_settimana}|${f.orario_inizio}|${f.orario_fine}|${f.tipo}`;
                  if (!map.has(key)) map.set(key, f);
                });
              });
              const orari = Array.from(map.values());

              const coverUrl =
                g.cover_url || g.logo_url || "/images/ciao_stiro.png";

              const hasLavaggio = g.items.some(
                (s) =>
                  s.id_categoria_servizio === 2 ||
                  s.id_categoria_servizio === 3,
              );
              const hasStiratura = g.items.some(
                (s) =>
                  s.id_categoria_servizio === 1 ||
                  s.id_categoria_servizio === 3,
              );

              return (
                <article
                  key={g.id_fornitore}
                  className="vendor pressable vendor--clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    goToScheda(
                      g.id_fornitore,
                      g.nome,
                      g.cover_url || g.logo_url,
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      goToScheda(
                        g.id_fornitore,
                        g.nome,
                        g.cover_url || g.logo_url,
                      );
                    }
                  }}
                >
                  <div className="cover">
                    <SupplierImage
                      src={coverUrl}
                      alt={g.nome}
                      onError={(e) => {
                        e.currentTarget.src = "/images/ciao_stiro.png";
                      }}
                    />
                    <div className="badge">Disponibilità fasce</div>
                  </div>

                  <div className="vendorBody">
                    <div className="vendorNameRow" title={g.nome}>
                      {g.nome}
                    </div>

                    {closureBadges.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                          marginTop: 8,
                          marginBottom: 6,
                        }}
                      >
                        {closureBadges.map((txt, idx) => (
                          <span
                            key={`${g.id_fornitore}-closure-${idx}`}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "4px 8px",
                              borderRadius: 999,
                              fontSize: 12,
                              fontWeight: 600,
                              background: "#fff4e5",
                              color: "#8a4b08",
                              border: "1px solid #f0d2a6",
                            }}
                          >
                            {txt}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="vendorMetaRow">
                      <button
                        className="rating-wrap as-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          setReviewsSupplier({
                            id: g.id_fornitore,
                            nome: g.nome,
                          });
                          setOpenReviews(true);
                        }}
                        title="Vedi recensioni"
                      >
                        <StarRating value={r.avg} count={r.count} />
                      </button>

                      {g.has_subscription_plans && (
                        <button
                          type="button"
                          className="rs-subscription-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVaiAbbonamenti(g);
                          }}
                        >
                          🎁 Abbonamenti
                        </button>
                      )}

                      <SupplierHoursButton
                        orari={orari}
                        label={g.nome}
                        isOpen={isOpenHours}
                        onToggle={() =>
                          setOpenHoursFor(isOpenHours ? null : g.id_fornitore)
                        }
                        onClose={() => setOpenHoursFor(null)}
                      />
                    </div>
                  </div>
                </article>
              );
            })
          ) : (
            <div className="empty-nocoverage">
              <div className="empty-nocoverage__illu" aria-hidden="true">
                <svg width="140" height="140" viewBox="0 0 140 140" fill="none">
                  {/* Dashed outer ring */}
                  <circle cx="70" cy="70" r="66" stroke="rgba(13,110,253,0.09)" strokeWidth="1.5" strokeDasharray="3 5"/>
                  {/* Soft fill circle */}
                  <circle cx="70" cy="70" r="52" fill="rgba(13,110,253,0.07)"/>

                  {/* Ghost pin — top right */}
                  <path d="M108 22c-3.3 0-6 2.7-6 6 0 3.7 6 10 6 10s6-6.3 6-10c0-3.3-2.7-6-6-6z" fill="#1d4ed8" opacity="0.14"/>
                  <circle cx="108" cy="28" r="2.5" fill="white"/>

                  {/* Ghost pin — left */}
                  <path d="M18 82c-4.4 0-8 3.6-8 8 0 4.8 8 13 8 13s8-8.2 8-13c0-4.4-3.6-8-8-8z" fill="#1d4ed8" opacity="0.12"/>
                  <circle cx="18" cy="90" r="3.5" fill="white"/>

                  {/* Main pin */}
                  <path d="M70 14C51.8 14 37 28.8 37 47C37 69 70 108 70 108C70 108 103 69 103 47C103 28.8 88.2 14 70 14Z" fill="#1d4ed8"/>
                  <circle cx="70" cy="47" r="16" fill="white"/>

                  {/* 4-pointed star inside pin */}
                  <path d="M70 37L73.9 43.1L80 47L73.9 50.9L70 57L66.1 50.9L60 47L66.1 43.1Z" fill="#1d4ed8" opacity="0.85"/>

                  {/* Sparkle accent — top right */}
                  <path d="M116 18l1.7 5 5 1.7-5 1.7-1.7 5-1.7-5-5-1.7 5-1.7z" fill="#1d4ed8" opacity="0.24"/>

                  {/* Dot accent — bottom right */}
                  <circle cx="120" cy="90" r="3" fill="#1d4ed8" opacity="0.1"/>
                </svg>
              </div>

              <h3 className="empty-nocoverage__title">
                La tua città è la prossima
              </h3>
              <p className="empty-nocoverage__body">
                Ci hai provato — e lo apprezziamo! Per ora non abbiamo ancora
                fornitori attivi nella tua zona, ma stiamo crescendo ogni settimana.
                Ti avvisiamo appena arriviamo vicino a te.
              </p>
              <button
                className="empty-nocoverage__cta pressable"
                onClick={() => navigate(-1)}
              >
                Torna indietro
              </button>
            </div>
          )}
        </div>
      )}

      <div className="spacer" />

      <ReviewsDrawer
        open={openReviews}
        onClose={() => setOpenReviews(false)}
        fornitoreId={reviewsSupplier?.id}
        fornitoreNome={reviewsSupplier?.nome}
      />
    </div>
  );
}
